import { getCurrentLanguage, t } from '../i18n'

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:3000/api'
const assetBaseUrl = apiBaseUrl.replace(/\/api\/?$/, '')
const mediaBaseUrl = (process.env.VUE_APP_MEDIA_BASE_URL || assetBaseUrl).replace(/\/$/, '')

function buildUrl (path, params = {}, options = {}) {
  const url = new URL(apiBaseUrl + path)
  const finalParams = {
    ...params
  }
  if (options.withLang !== false) {
    finalParams.lang = finalParams.lang || getCurrentLanguage()
  }
  Object.keys(finalParams).forEach(key => {
    if (finalParams[key] !== undefined && finalParams[key] !== null && finalParams[key] !== '') {
      url.searchParams.set(key, finalParams[key])
    }
  })
  return url.toString()
}

export function resolveAssetUrl (fileUrl, fallback = '') {
  if (!fileUrl) return fallback
  if (/^https?:\/\//.test(fileUrl)) return fileUrl
  if (fileUrl.startsWith('/assets/') || fileUrl.startsWith('/uploads/')) return `${mediaBaseUrl}${fileUrl}`
  return `${assetBaseUrl}${fileUrl}`
}

export function fetchPublicCategories (params = {}) {
  return fetch(buildUrl('/products/categories', {
    status: 'active',
    ...params
  })).then(response => {
    if (!response.ok) throw new Error('Failed to load product categories.')
    return response.json()
  })
}

export function fetchPublicProducts (params = {}) {
  return fetch(buildUrl('/products', {
    status: 'active',
    ...params
  })).then(response => {
    if (!response.ok) throw new Error('Failed to load products.')
    return response.json()
  })
}

export function fetchPublicProduct (slug) {
  return fetch(buildUrl(`/products/${slug}`)).then(response => {
    if (!response.ok) throw new Error('Failed to load product.')
    return response.json()
  })
}

export function fetchPublicPageSections (params = {}) {
  return fetch(buildUrl('/page-sections', {
    status: 'active',
    ...params
  })).then(response => {
    if (!response.ok) throw new Error('Failed to load page sections.')
    return response.json()
  })
}

export function fetchPublicSiteSettings () {
  return fetch(buildUrl('/site/settings')).then(response => {
    if (!response.ok) throw new Error('Failed to load site settings.')
    return response.json()
  })
}

export function fetchPublicCases (params = {}) {
  return fetch(buildUrl('/cases', {
    status: 'active',
    ...params
  })).then(response => {
    if (!response.ok) throw new Error('Failed to load cases.')
    return response.json()
  })
}

export function fetchPublicCase (slug) {
  return fetch(buildUrl(`/cases/${slug}`)).then(response => {
    if (!response.ok) throw new Error('Failed to load case.')
    return response.json()
  })
}

export function fetchPublicArticles (params = {}) {
  return fetch(buildUrl('/articles', {
    status: 'active',
    ...params
  })).then(response => {
    if (!response.ok) throw new Error('Failed to load articles.')
    return response.json()
  })
}

export function fetchPublicArticle (slug) {
  return fetch(buildUrl(`/articles/${slug}`)).then(response => {
    if (!response.ok) throw new Error('Failed to load article.')
    return response.json()
  })
}

export function submitInquiry (payload) {
  return fetch(buildUrl('/inquiries', {}, { withLang: false }), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).then(response => {
    if (!response.ok) throw new Error('Failed to submit inquiry.')
    return response.json()
  })
}

export function submitCustomRequest (payload) {
  return fetch(buildUrl('/custom-requests', {}, { withLang: false }), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  }).then(response => {
    if (!response.ok) throw new Error('Failed to submit custom request.')
    return response.json()
  })
}

export function normalizeProduct (item, fallbackImage = '') {
  const image = resolveAssetUrl(item.image || item.coverImage || item.heroImage, fallbackImage)
  const heroImage = resolveAssetUrl(item.heroImage || item.image || item.coverImage, image)
  const gallery = Array.isArray(item.galleryJson)
    ? item.galleryJson.map(url => resolveAssetUrl(url)).filter(Boolean)
    : []
  const color = item.color || item.colour || ''
  const topLayer = item.topLayer || item.thickness || ''

  return {
    ...item,
    color,
    topLayer,
    category: item.category ? item.category.name : '',
    categorySlug: item.category ? item.category.slug : '',
    image,
    heroImage,
    gallery,
    summary: item.summary || item.intro || item.description || '',
    specs: [
      { label: t('product.species'), value: item.species },
      { label: t('product.size'), value: item.size },
      { label: t('product.topLayer'), value: topLayer },
      { label: t('product.color'), value: color },
      { label: t('product.finish'), value: item.finish },
      { label: t('product.grading'), value: item.grading },
      { label: t('product.surface'), value: item.surface },
      { label: t('product.installation'), value: item.installation }
    ].filter(spec => spec.value)
  }
}

export function normalizeCase (item, fallbackImage = '') {
  const image = resolveAssetUrl(item.coverImage || item.heroImage, fallbackImage)
  const heroImage = resolveAssetUrl(item.heroImage || item.coverImage, image)
  const gallery = Array.isArray(item.gallery)
    ? item.gallery.map(galleryItem => resolveAssetUrl(galleryItem.imageUrl || galleryItem.image)).filter(Boolean)
    : []
  const bodyLines = String(item.body || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  return {
    ...item,
    image,
    heroImage,
    eyebrow: t('common.reference'),
    subtitle: item.intro || item.country || '',
    summary: item.intro || item.body || '',
    notes: bodyLines,
    specs: [
      { label: t('common.country'), value: item.country },
      { label: t('common.reference'), value: item.title }
    ].filter(spec => spec.value),
    outcomes: [],
    gallery: gallery.length ? gallery : [image].filter(Boolean)
  }
}

export function normalizeArticle (item, fallbackImage = '') {
  const image = resolveAssetUrl(item.coverImage || item.image, fallbackImage)
  const body = Array.isArray(item.body)
    ? item.body
    : String(item.content || '')
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)

  return {
    ...item,
    image,
    dateLabel: item.dateLabel || '',
    excerpt: item.excerpt || '',
    body
  }
}
